/**
 * MANAGER-ROLLUP-REACHABILITY-V1 (activity 11 closure) -- the route guard for
 * `/dashboard/callcenter/daily-report`.
 *
 * Pure by construction (no React, no fetch, no Supabase, no Date.now()) so the
 * access decision unit-tests without jsdom -- mirrors
 * decideManagerTrackingAccess (manager-tracking-access.ts).
 *
 * WHY THIS EXISTS: the route was guarded by `RequireNavSection
 * section="call_center"`, whose decision keys off `me.navSections` -- the
 * SAME array that drives the left-nav render. Only `call_center_agent` carries
 * the `call_center` section (`dashboardNavSectionsForRole`), so that guard
 * redirected owner, gm_agm, AND regional_head away before the page ever
 * mounted, even though AGENT-DAY-PRODUCTIVITY-ROLLUP-V1 already composed the
 * leadership tier's rollup onto this SAME route server-side
 * (CALLCENTER_DAILY_ROLLUP_AUDIENCE_ROLES, api/routes/callcenter-daily-report.ts).
 * This is the identical class of gap CRM-ROLE-VISIBILITY-DOCTRINE-V1 +
 * OWNER-READ-DOWN already solved for the manager tracking landing
 * (manager-tracking-access.ts): a per-surface, role-set gate that DECOUPLES
 * route visibility from nav-section membership.
 *
 * This set is the frontend twin of the backend's COMBINED audience for
 * GET /api/callcenter/daily-report: the agent-facing
 * CALLCENTER_DAILY_REPORT_AUDIENCE_ROLES (call_center_agent, UNCHANGED) UNION
 * the manager-rollup CALLCENTER_DAILY_ROLLUP_AUDIENCE_ROLES (owner, gm_agm,
 * regional_head -- the exact MANAGER_TRACKING_AUDIENCE_ROLES set). Kept in
 * lockstep by review, same as manager-tracking-access.ts's own frontend twin.
 *
 * SEE-NOT-DO (doctrine rule 2): a READ gate only -- no claim / assign /
 * qualify / stage authority on this surface. Backend route auth
 * (getCallcenterDailyReport's own audience check) remains the real security
 * boundary; this guard is UX cleanliness only.
 */

import type { DashboardMe } from '@/dashboard/types';

/**
 * Every role admitted to `/dashboard/callcenter/daily-report` today: the
 * call-centre agent's own-day view (unchanged) plus the leadership tier's
 * team rollup (newly reachable). Role-static -- this route carries no
 * per-org config gate (CALLCENTRE-EOD-V1 shipped it flag-free).
 */
export const CALLCENTER_DAILY_REPORT_ROUTE_AUDIENCE_ROLES: ReadonlySet<
  DashboardMe['role']
> = new Set<DashboardMe['role']>([
  'call_center_agent',
  'call_center_manager',
  'owner',
  'gm_agm',
  'regional_head',
]);

export type CallCenterDailyReportAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Pure access decision for the call-centre Daily report route guard. Mirrors
 * decideManagerTrackingAccess: wait while loading; redirect to /login on
 * error or no scope; redirect to the caller's own homePath when the role is
 * outside the combined audience; otherwise render.
 */
export function decideCallCenterDailyReportAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): CallCenterDailyReportAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if ((state.me.role === 'call_center_manager' && state.me.callCenterWorkspaceEnabled !== true) || !CALLCENTER_DAILY_REPORT_ROUTE_AUDIENCE_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}
